"use client";
import React, { useState, useEffect } from "react";
import { getSidebarMenuList, resolveText } from "./constants";
import Link from "next/link";
import TypographyPoppins from "../../Atoms/TypographyPoppins";
import { usePathname } from "next/navigation";

import Image from "next/image";
import useStore from "../../../lib/Store/useStore";
import { useResetAllStores } from "../../../lib/Hooks/useResetAllStores";
import FullScreenOverlay from "../../Organisms/FullScreenLoader";
import surveyDropoffCall from "../../../utils/surveyDropoffCall";
import ProfileMenuLoginSignup from "../../Atoms/ProfileMenuLoginSignup";
import ProfileMenuUserName from "../../Atoms/ProfileMenuUserName";
import { AnimatePresence, motion } from "framer-motion";

import { CloseIcon } from "../../../public/Theme";
import DashboardMenuCopyright from "@/components/Atoms/DashboardMenuCopyright";

const SidebarLink = ({ text, setOpen, id, link, icon }) => {
    const pathName = usePathname()
    return (
        <Link
            onClick={() => setOpen(false)}
            testid={`avatar-menu-${id}`}
            href={link}
            prefetch
            className="flex gap-3"
        >
            <Image width={25} height={25} src={icon} alt={text} />
            <TypographyPoppins
                size={"base"}
                fontWeight={"light"}
                text={resolveText(text, pathName)}
                customStyle={"!capitalize"}
            />
        </Link>
    )
};

const DashboardSmallMenu = ({ userData, open, setOpen }) => {
    const [loggingOut, setLoggingOut] = useState(false);
    const pathName = usePathname();
    const menuItems = getSidebarMenuList(userData);
    const resetAllStores = useResetAllStores();
    const {
        surveyAnswers,
        surveyQuestions,
        createUserSurvey,
        surveyInfo,
        flag,
    } = useStore();

    const handleLogout = async () => {
        setLoggingOut(true);
        if (!userData?.submitted_survey && !!Object.keys(surveyAnswers).length) {
            await surveyDropoffCall({
                createUserSurvey,
                surveyAnswers,
                surveyQuestions,
                surveyInfo,
                flag,
            });
        }
        resetAllStores({ logout: true });
    };

    // Disable scrolling when the menu is open
    useEffect(() => {
        if (open) {
            document.body.classList.add("no-scroll");
        } else {
            document.body.classList.remove("no-scroll");
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove("no-scroll");
        };
    }, [open]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0, x: "100%" }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: "100%" }}
                    transition={{ duration: 0.3 }}
                    className="fixed lg:absolute z-[600] inset-0 lg:h-fit lg:-left-[250px] top-[60px] lg:top-[48px] w-screen lg:w-[375px] overflow-y-auto bg-white drop-shadow-4xl"
                >
                    {loggingOut && <FullScreenOverlay />}
                    <div className="h-[calc(100vh-160px)] lg:h-auto">
                        {/* Header Section */}
                        <div className="relative">
                            <button
                                onClick={() => setOpen(false)}
                                className="absolute top-2 right-2"
                            >
                                <Image
                                    width={25}
                                    height={25}
                                    src={CloseIcon}
                                    alt={"close icon"}
                                />
                            </button>
                            <div className="flex items-center gap-3">
                                {userData ? (
                                    <ProfileMenuUserName
                                        firstName={userData?.user?.first_name}
                                        lastName={userData?.user?.last_name}
                                    />
                                ) : (
                                    <ProfileMenuLoginSignup setOpen={setOpen} />
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col justify-between h-full">
                            {/* Menu Items */}
                            <div className="flex flex-col gap-4 p-6">
                                {menuItems?.map(({ id, icon, text, link }) => {
                                    if (text === "Logout") {
                                        return (
                                            <div
                                                key={id}
                                                onClick={() => {
                                                    handleLogout();
                                                    setOpen(false);
                                                }}
                                                data-testid={`avatar-menu-${id}`}
                                                className="flex gap-3 cursor-pointer"
                                            >
                                                <Image
                                                    width={25}
                                                    height={25}
                                                    src={icon}
                                                    alt={text}
                                                />
                                                <TypographyPoppins
                                                    size="base"
                                                    fontWeight="light"
                                                    text={text}
                                                    customStyle="!capitalize"
                                                />
                                            </div>
                                        );
                                    }

                                    return (
                                        <SidebarLink
                                            key={id}
                                            text={text}
                                            setOpen={setOpen}
                                            id={id}
                                            link={link}
                                            icon={icon}
                                        />
                                    );
                                })}
                            </div>
                            {/* Privay & Terms And Conditions */}
                            <div className="ml-5 mb-7 mt-3">
                                <DashboardMenuCopyright />
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default DashboardSmallMenu;
